// TODO: Remove TimeLog component — time logs are now managed declaratively via reactive-json
// in TaskEdit.jsx. Only UserSearchInput is still used (for task assignee field).
import React, { useState } from 'react';
import { initials } from '../shared/ui/InitialsAvatar';

export const UserSearchInput = ({ id, value, onChange, placeholder, users = [] }) => {
    const [search, setSearch] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    
    // Initialize search with value if it exists and we aren't searching
    React.useEffect(() => {
        if (value && !search) {
            setSearch(value);
        }
        // If value is cleared externally, we might want to clear search too, but be careful of loop
        if (value === "" && search !== "") {
             setSearch("");
        }
    }, [value]);

    const filteredUsers = Array.isArray(users) ? users.filter(user => 
        (user.name || "").toLowerCase().includes((search || "").toLowerCase())
    ) : [];

    return (
        <div className="relative w-full">
            <input 
                type="text" 
                id={id}
                value={search} // Controlled by local search state which syncs with value
                onChange={(e) => {
                    const val = e.target.value;
                    setSearch(val);
                    onChange(val); // Allow free text too
                    setShowDropdown(true);
                }}
                onFocus={() => {
                    setShowDropdown(true);
                }}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                className="block w-full text-sm border-none focus:ring-0 rounded-md bg-transparent px-3 py-2"
                placeholder={placeholder}
            />
            {showDropdown && (search || filteredUsers.length > 0) && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto left-0">
                    {filteredUsers.length > 0 ? (
                        filteredUsers.map(user => (
                            <div 
                                key={user.id} 
                                className="px-4 py-2 hover:bg-iris-50 cursor-pointer flex items-center gap-2 transition-colors"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onChange(user.name);
                                    setSearch(user.name); 
                                    setShowDropdown(false);
                                }}
                            >
                                <div className="avatar avatar-sm text-iris-600 flex-shrink-0">
                                    {initials(user.name)}
                                </div>
                                <div className="text-sm text-slate-900">{user.name}</div>
                            </div>
                        ))
                    ) : (
                         <div className="px-4 py-2 text-sm text-slate-500 italic">Aucun utilisateur trouvé</div>
                    )}
                </div>
            )}
        </div>
    );
};

export const TimeLog = ({ logs = [], allocatedTime, onLogsChange, users = [], onApiSave, defaultValues }) => {
    const [timeLogs, setTimeLogs] = useState(logs);
    const [newTimeLog, setNewTimeLog] = useState({
        date: defaultValues?.date || (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
        user: defaultValues?.user || "",
        description: "",
        timeSpent: "",
        timeLeft: ""
    });
    
    // Edit state
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingLog, setEditingLog] = useState(null);

    // Filter users who can log time
    const eligibleUsers = Array.isArray(users) ? users.filter(user => user.canLogTime !== false) : [];

    const handleAddLog = async () => {
        if (newTimeLog.timeSpent && newTimeLog.description && newTimeLog.user) {
            const updatedLogs = [...timeLogs, newTimeLog];
            setTimeLogs(updatedLogs);
            if (onLogsChange) onLogsChange(updatedLogs);

            if (onApiSave) {
                try { await onApiSave(newTimeLog); }
                catch (e) { console.error('Error saving time log:', e); }
            }

            const currentLeft = parseFloat(newTimeLog.timeLeft);
            setNewTimeLog({
                ...newTimeLog,
                description: "",
                timeSpent: "",
                timeLeft: currentLeft
            });
        }
    };

    const handleEditClick = (idx, log) => {
        // Since we are reversing the list for display, the index passed here is from the reversed list.
        // But we want to edit the item in the original list.
        // Wait, the map below is doing [...timeLogs].reverse().map...
        // So 'idx' 0 corresponds to the LAST item in 'timeLogs'.
        
        const realIndex = timeLogs.length - 1 - idx;
        setEditingIndex(realIndex);
        setEditingLog({ ...log }); // Clone to edit
    };

    const handleSaveEdit = () => {
        if (editingIndex !== null && editingLog) {
            const updatedLogs = [...timeLogs];
            updatedLogs[editingIndex] = editingLog;
            setTimeLogs(updatedLogs);
            
            if (onLogsChange) {
                onLogsChange(updatedLogs);
            }
            
            setEditingIndex(null);
            setEditingLog(null);
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditingLog(null);
    };

    return (
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-slate-200/75">
            <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                Suivi des temps
            </h3>

            {/* Add New Log Form */}
            <div className="bg-slate-50 p-5 rounded-lg border border-slate-200 mb-6">
                <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Ajouter une entrée</h4>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-slate-500 mb-1">Date</label>
                        <input 
                            type="date"
                            value={newTimeLog.date}
                            onChange={(e) => setNewTimeLog({...newTimeLog, date: e.target.value})}
                            className="input"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-slate-500 mb-1">Utilisateur <span className="text-red-500">*</span></label>
                        <div className="border-2 border-slate-300 rounded-md bg-white focus-within:ring-2 focus-within:ring-iris-500 focus-within:border-iris-500">
                            <UserSearchInput 
                                value={newTimeLog.user}
                                onChange={(val) => setNewTimeLog({...newTimeLog, user: val})}
                                placeholder="Nom..."
                                users={eligibleUsers}
                            />
                        </div>
                    </div>
                    <div className="md:col-span-4">
                        <label className="block text-xs font-medium text-slate-500 mb-1">Description <span className="text-red-500">*</span></label>
                        <input 
                            type="text" 
                            value={newTimeLog.description}
                            onChange={(e) => setNewTimeLog({...newTimeLog, description: e.target.value})}
                            placeholder="Description du travail..."
                            className="input"
                        />
                    </div>
                    <div className="md:col-span-1">
                        <label className="block text-xs font-medium text-slate-500 mb-1 text-right">Passé (h) <span className="text-red-500">*</span></label>
                        <input 
                            type="number" 
                            value={newTimeLog.timeSpent}
                            onChange={(e) => {
                                const spent = parseFloat(e.target.value);
                                const prevLeft = timeLogs.length > 0 
                                    ? parseFloat(timeLogs[timeLogs.length - 1].timeLeft || 0) 
                                    : parseFloat(allocatedTime || 0);
                                
                                const newLeft = !isNaN(spent) ? (prevLeft - spent).toFixed(2) : prevLeft;
                                
                                setNewTimeLog({
                                    ...newTimeLog, 
                                    timeSpent: e.target.value,
                                    timeLeft: newLeft > 0 ? newLeft : 0
                                });
                            }}
                            className="input text-right"
                            step="0.25"
                        />
                    </div>
                    <div className="md:col-span-1">
                        <label className="block text-xs font-medium text-slate-500 mb-1 text-right">Reste (h)</label>
                        <input 
                            type="number" 
                            value={newTimeLog.timeLeft}
                                onChange={(e) => setNewTimeLog({...newTimeLog, timeLeft: e.target.value})}
                                className="input text-right"
                                step="0.25"
                            />
                    </div>
                    <div className="md:col-span-2">
                            <button
                            onClick={handleAddLog}
                            disabled={!newTimeLog.timeSpent || !newTimeLog.description || !newTimeLog.user}
                            className="btn btn-primary btn-block"
                        >
                            Ajouter
                        </button>
                    </div>
                </div>
            </div>

            {/* Log List Table */}
            <div className="overflow-hidden border border-slate-200 rounded-lg">
                <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Utilisateur</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Description</th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Temps passé (h)</th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Reste à faire (h)</th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                        {timeLogs.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-8 text-center text-sm text-slate-500 italic">
                                    Aucun temps saisi pour le moment.
                                </td>
                            </tr>
                        ) : (
                            [...timeLogs].reverse().map((log, idx) => {
                                const realIndex = timeLogs.length - 1 - idx;
                                const isEditing = editingIndex === realIndex;

                                return (
                                <tr key={realIndex} className={`hover:bg-slate-50 transition-colors ${isEditing ? 'bg-iris-50' : ''}`}>
                                    {isEditing ? (
                                        <>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <input 
                                                    type="date"
                                                    value={editingLog.date}
                                                    onChange={(e) => setEditingLog({...editingLog, date: e.target.value})}
                                                    className="input text-xs"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <input 
                                                    type="text"
                                                    value={editingLog.user}
                                                    onChange={(e) => setEditingLog({...editingLog, user: e.target.value})}
                                                    className="input text-xs"
                                                />
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <input 
                                                    type="text"
                                                    value={editingLog.description}
                                                    onChange={(e) => setEditingLog({...editingLog, description: e.target.value})}
                                                    className="input text-xs"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                                <input 
                                                    type="number"
                                                    value={editingLog.timeSpent}
                                                    onChange={(e) => setEditingLog({...editingLog, timeSpent: e.target.value})}
                                                    className="input text-xs text-right"
                                                    step="0.25"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                                <input 
                                                    type="number"
                                                    value={editingLog.timeLeft}
                                                    onChange={(e) => setEditingLog({...editingLog, timeLeft: e.target.value})}
                                                    className="input text-xs text-right"
                                                    step="0.25"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={handleSaveEdit} className="text-green-600 hover:text-green-900">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                                    </button>
                                                    <button onClick={handleCancelEdit} className="text-red-600 hover:text-red-900">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{log.date}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-medium">{log.user}</td>
                                            <td className="px-6 py-4 text-sm text-slate-500">{log.description}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-slate-900 font-medium">{log.timeSpent}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-slate-500">{log.timeLeft}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button 
                                                    onClick={() => handleEditClick(idx, log)} 
                                                    className="text-slate-400 hover:text-iris-600 transition-colors"
                                                    title="Modifier"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                </button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
